import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MobileAppBadges from '../components/MobileAppBadges';
import LandingLayout from '../components/LandingLayout';
import { useForm } from 'react-hook-form';
import { useAuthMutation } from '../../../hooks/Auth';
import { useAuthState } from '../../../redux/selector';
import SocialLogin from '../components/SocialLogin';
import EmailPasswordForm, { EmailPasswordFormValues } from '../components/EmailPasswordForm';
import SectionDivider from '../components/SectionDivider';
import ErrorMessage from '../components/ErrorMessage';
import { updateAuthState } from '../../../redux/actions';
import { useDispatch } from 'react-redux';

export const COMMUNITY_NAME_QUERY_PARAM = 'communityName';
const SENDER_NAME_QUERY_PARAM = 'sender';
const RECIPIENT_NAME_QUERY_PARAM = 'recipient';
const RECIPIENT_EMAIL_QUERY_PARAM = 'recipientEmailAddress';

const Register = () => {
  const showLanding = useShowLanding();
  const email = useEmail() ?? undefined;
  const dispatch = useDispatch();
  const { control, handleSubmit } = useForm<EmailPasswordFormValues>({
    defaultValues: {
      email,
    },
  });
  const { useSignUpWithEmailAndPassword } = useAuthMutation();
  const onSubmit = async (values: EmailPasswordFormValues) => {
    dispatch(updateAuthState({ email: values.email }));
    await useSignUpWithEmailAndPassword.mutateAsync(values);
  };
  return (
    <LandingLayout showLanding={showLanding}>
      <div className="heading-s">
        <WelcomeMessage />
      </div>
      <ErrorMessage />
      <EmailPasswordForm control={control} submitText="Sign up" onSubmit={handleSubmit(onSubmit)} />
      <SectionDivider />
      <div className="d-flex flex-column justify-content-center">
        <SocialLogin />
      </div>
      <div className="py-2" />
      <p className="text-content-secondary">
        Already using Forest?{' '}
        <Link to="/auth/login">
          <b>Log in</b>
        </Link>
      </p>
      <MobileAppBadges />
    </LandingLayout>
  );
};

const WelcomeMessage = () => {
  const { redirectQueryParams } = useAuthState();
  const query = new URLSearchParams(redirectQueryParams);
  const communityName = query.get(COMMUNITY_NAME_QUERY_PARAM);
  const sender = query.get(SENDER_NAME_QUERY_PARAM);
  const recipient = query.get(RECIPIENT_NAME_QUERY_PARAM);
  if (!communityName) {
    return <>Let's get you set up</>;
  }
  return (
    <>
      {`Hi ${recipient ?? 'Hi there'}, `}
      {`${sender ? sender : 'community admin'} invited you to join ${communityName ?? 'Forest'}`}
    </>
  );
};

/**
 * Show a mini landing page on the side if the user came from an invitation link.
 */
const useShowLanding = () => {
  const communityName = useCommunityName();
  return !!communityName;
};

const useEmail = () => {
  const { email, redirectQueryParams } = useAuthState();
  const query = new URLSearchParams(redirectQueryParams);
  const recipientEmail = query.get(RECIPIENT_EMAIL_QUERY_PARAM);
  const dispatch = useDispatch();
  useEffect(() => {
    if (recipientEmail) {
      dispatch(updateAuthState({ email: recipientEmail }));
    }
  }, [dispatch, recipientEmail]);
  return recipientEmail ?? email;
};

const useCommunityName = () => {
  const { redirectQueryParams } = useAuthState();
  const query = new URLSearchParams(redirectQueryParams);
  return query.get(COMMUNITY_NAME_QUERY_PARAM);
};

export default Register;
